/**
 * Setting the gross profit margin, and seeing what it does before it does it.
 *
 * Jonathon, 2026-09-15: "it also needs to be more user friendly on the gross
 * profit percentage adjustment. often times sam tries to adjust it but it
 * won't let him."
 *
 * What was here was a `window.prompt` behind a 40×18px pill, and three
 * `window.alert`s. Four things were wrong with it and this fixes all four,
 * because which one Sam was hitting is not something anybody can tell from
 * the outside:
 *
 *  1. **The browser could swallow it.** A `prompt` in an installed PWA is at
 *     the browser's discretion, and once "prevent additional dialogs" has been
 *     ticked it returns null for ever. The click did nothing, no message, no
 *     way to tell that from the app ignoring him.
 *  2. **The target was a pill of text.** Underlined dotted, with a `title`
 *     tooltip that a phone never shows. Now a button, with the word on it, big
 *     enough to hit.
 *  3. **It refused without saying what would work.** "That margin is not
 *     reachable" is a dead end; the answer — a *higher* margin — is the
 *     opposite of what anybody guesses, and now the number is on a button.
 *  4. **Nothing was visible until it had happened.** The new contract total,
 *     and what it moves by, are on screen as he types.
 *
 * No arithmetic here. `planReprice` works out every price and this shows it —
 * rule 3, and the reason the preview cannot disagree with what Apply writes.
 */

import {
  formatMoney,
  formatPercent,
  lowestReachableMargin,
  marginIsInRange,
  parseMarginPercent,
  planReprice,
  repriceMove,
  repriceRefusalText,
  type LadderInput,
  type RepricePlan,
  type RepriceTotals,
  type RepriceableLine,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { Modal } from "../../components/ui";
import { t, tn } from "../../i18n";

/** The steps offered beside the box. The company sells at 40%. */
const PRESETS = [30, 35, 40, 45, 50];

export interface MarginModalProps {
  /** Everything the ladder needs, so the preview is the real number. */
  input: LadderInput;
  totals: RepriceTotals;
  lines: readonly RepriceableLine[];
  /** The margin now, 0–1. */
  current: number;
  onClose: () => void;
  onApply: (plan: RepricePlan) => Promise<void>;
}

export function MarginModal({
  input,
  totals,
  lines,
  current,
  onClose,
  onApply,
}: MarginModalProps) {
  // Opened on the margin it already has, to one decimal — the number he is
  // most likely to be nudging rather than replacing.
  const [typed, setTyped] = useState(String(Math.round(current * 1000) / 10));
  const [busy, setBusy] = useState(false);

  const percent = parseMarginPercent(typed);
  const plan = useMemo(
    () =>
      percent === null ? null : planReprice(input, totals, lines, percent),
    [input, totals, lines, percent],
  );

  // Only worked out when it is needed: it reprices the job up to ninety-nine
  // times to find the answer, which is nothing on a click and not free on a
  // keystroke.
  const lowest = useMemo(
    () =>
      plan?.refusal === "change_orders_exceed_target"
        ? lowestReachableMargin(input, totals, lines)
        : null,
    [plan?.refusal, input, totals, lines],
  );

  const nudge = (by: number) => {
    const from = percent ?? Math.round(current * 1000) / 10;
    const next = Math.min(99, Math.max(1, Math.round((from + by) * 10) / 10));
    setTyped(String(next));
  };

  const ready = plan !== null && plan.refusal === null && !busy;
  const move = plan && plan.refusal === null ? repriceMove(plan) : null;

  return (
    <Modal
      title={t("Gross Profit Margin")}
      subtitle={t(
        "Set the margin and the line items are repriced to match. Change orders and discounts are left as they are.",
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-small" onClick={onClose} disabled={busy}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            disabled={!ready}
            onClick={() => {
              if (!plan || plan.refusal !== null) return;
              setBusy(true);
              void onApply(plan).finally(() => setBusy(false));
            }}
          >
            {busy ? t("Repricing…") : t("Reprice")}
          </button>
        </>
      }
    >
      <div className="marginset">
        <div className="mrow">
          <button
            className="mstep"
            aria-label={t("Down one percent")}
            onClick={() => nudge(-1)}
          >
            −
          </button>
          <input
            className="mbox"
            inputMode="decimal"
            autoFocus
            aria-label={t("Gross profit margin")}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
          <span className="mpct">%</span>
          <button
            className="mstep"
            aria-label={t("Up one percent")}
            onClick={() => nudge(1)}
          >
            +
          </button>
        </div>

        <div className="mpresets">
          {PRESETS.map((p) => (
            <button
              key={p}
              className={`mpreset${percent === p ? " on" : ""}`}
              onClick={() => setTyped(String(p))}
            >
              {p}%
            </button>
          ))}
        </div>

        {/* What it does, before it does it. The contract is the number the
            customer sees, so that is the one shown moving. */}
        {plan && plan.refusal === null && move ? (
          <div className="mpreview">
            <div className="mline">
              <span className="k">{t("Contract now")}</span>
              <span className="v">{formatMoney(plan.wasGrandTotal)}</span>
            </div>
            <div className="mline">
              <span className="k">{t("Becomes")}</span>
              <span className="v strong">{formatMoney(plan.grandTotal)}</span>
            </div>
            <div className="mline">
              <span className="k">
                {move.direction === "up" ? t("Goes up by") : t("Comes down by")}
              </span>
              <span className="v">{formatMoney(move.by)}</span>
            </div>
            <div className="na-hint">
              {tn(
                plan.lines.length,
                "1 line repriced — currently at {pct}.",
                "{n} lines repriced — currently at {pct}.",
                { pct: formatPercent(current) },
              )}
            </div>
          </div>
        ) : (
          <div className="mrefusal" role="status">
            {percent === null || !marginIsInRange(percent)
              ? t("A margin has to be between 0 and 100.")
              : t(repriceRefusalText(plan!.refusal!, lowest))}
            {/* The number that does work, on a button. The alert this
                replaces said only that the number he typed did not. */}
            {lowest !== null ? (
              <button
                className="btn-small"
                style={{ marginLeft: 8 }}
                onClick={() => setTyped(String(lowest))}
              >
                {t("Use {pct}%", { pct: lowest })}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </Modal>
  );
}
